import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";

const empty = {
    title: "",
    excerpt: "",
    content: "",
    cover_image: "",
    tags: "",
    published: true,
};

export default function AdminBlogEditor() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const [data, setData] = useState(empty);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!isEdit) return;
        api.get(`/admin/blog/${id}`)
            .then((r) =>
                setData({
                    ...r.data,
                    tags: (r.data.tags || []).join(", "),
                }),
            )
            .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || err.message))
            .finally(() => setLoading(false));
    }, [id, isEdit]);

    const update = (k, v) => setData((s) => ({ ...s, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const body = {
            ...data,
            tags: typeof data.tags === "string"
                ? data.tags.split(",").map((t) => t.trim()).filter(Boolean)
                : data.tags,
        };
        try {
            if (isEdit) {
                await api.patch(`/admin/blog/${id}`, body);
                toast.success("Yazı güncellendi.");
            } else {
                await api.post("/admin/blog", body);
                toast.success("Yazı oluşturuldu.");
            }
            navigate("/admin/blog");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <p className="p-10 text-zinc-500">Yükleniyor...</p>;

    return (
        <div className="p-6 md:p-10 max-w-[900px]">
            <Link to="/admin/blog" className="text-xs text-zinc-400 hover:text-cyan-300 mb-4 inline-block">
                ← Tüm yazılar
            </Link>
            <h1 className="font-display text-3xl text-white mb-8">
                {isEdit ? "Yazıyı Düzenle" : "Yeni Yazı"}
            </h1>
            <form onSubmit={submit} className="space-y-5" data-testid="blog-editor-form">
                <div className="space-y-2">
                    <Label className="text-zinc-300 text-xs uppercase tracking-wider">Başlık *</Label>
                    <Input
                        value={data.title}
                        onChange={(e) => update("title", e.target.value)}
                        required
                        className="bg-[#141414] border-white/10 text-white"
                        data-testid="blog-title"
                    />
                </div>
                <div className="space-y-2">
                    <Label className="text-zinc-300 text-xs uppercase tracking-wider">Özet</Label>
                    <Textarea
                        value={data.excerpt}
                        onChange={(e) => update("excerpt", e.target.value)}
                        rows={3}
                        className="bg-[#141414] border-white/10 text-white resize-none"
                        data-testid="blog-excerpt"
                    />
                </div>
                <div className="space-y-2">
                    <Label className="text-zinc-300 text-xs uppercase tracking-wider">Kapak Görsel URL</Label>
                    <Input
                        value={data.cover_image}
                        onChange={(e) => update("cover_image", e.target.value)}
                        placeholder="https://..."
                        className="bg-[#141414] border-white/10 text-white"
                        data-testid="blog-cover"
                    />
                </div>
                <div className="space-y-2">
                    <Label className="text-zinc-300 text-xs uppercase tracking-wider">
                        Etiketler (virgülle ayırın)
                    </Label>
                    <Input
                        value={data.tags}
                        onChange={(e) => update("tags", e.target.value)}
                        placeholder="pico lazer, dövme silme"
                        className="bg-[#141414] border-white/10 text-white"
                        data-testid="blog-tags"
                    />
                </div>
                <div className="space-y-2">
                    <Label className="text-zinc-300 text-xs uppercase tracking-wider">
                        İçerik (Markdown destekler: ## başlık, - madde)
                    </Label>
                    <Textarea
                        value={data.content}
                        onChange={(e) => update("content", e.target.value)}
                        required
                        rows={18}
                        className="bg-[#141414] border-white/10 text-white resize-none font-mono text-sm"
                        data-testid="blog-content"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Checkbox
                        id="published"
                        checked={data.published}
                        onCheckedChange={(v) => update("published", !!v)}
                        className="border-white/20 data-[state=checked]:bg-cyan-400 data-[state=checked]:border-cyan-400"
                        data-testid="blog-published"
                    />
                    <Label htmlFor="published" className="text-sm text-zinc-300 cursor-pointer">
                        Yayında
                    </Label>
                </div>
                <div className="flex gap-3 pt-3">
                    <Button
                        type="submit"
                        disabled={saving}
                        className="bg-cyan-400 text-black hover:bg-cyan-300 font-medium"
                        data-testid="blog-save"
                    >
                        {saving ? "Kaydediliyor..." : isEdit ? "Güncelle" : "Yayınla"}
                    </Button>
                    <Link to="/admin/blog">
                        <Button
                            type="button"
                            variant="outline"
                            className="bg-transparent border-white/10 text-white hover:bg-white/5"
                        >
                            İptal
                        </Button>
                    </Link>
                </div>
            </form>
        </div>
    );
}
